import axios from "axios";
import ReactGA from "react-ga4";
import { getGATRackingId } from "../config";
import { refreshUserSession, isTokenExpired } from "../utils/helper";
const gaID = getGATRackingId();
ReactGA.initialize(gaID);
const webAppType = 1; // FOR WEB

let refreshPromise = null;

const getRefreshedToken = () => {
  if (!refreshPromise) {
    refreshPromise = refreshUserSession()
      .then((refreshed) => {
        if (refreshed?.idToken) {
          localStorage.setItem("id_token", refreshed.idToken);
          localStorage.setItem("access_token", refreshed.accessToken);
        }
        return refreshed;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// An expired token is rejected by the API gateway authorizer with a 401 that has
// no CORS headers, so the browser reports a CORS error and axios never sees the
// 401. Refresh before sending instead of relying on the 401 response.
const ensureFreshToken = async (params) => {
  const token = params?.headers?.Authorization;
  if (!token || !isTokenExpired(token)) return;
  const refreshed = await getRefreshedToken();
  if (refreshed?.idToken) {
    params.headers = { ...params.headers, Authorization: refreshed.idToken };
  }
};

export const get = async (url, params, isAutoSearch = false) => {
  try {
    if (!isAutoSearch) {
      params.headers = { ...params.headers, AppTypeId: webAppType };
    }
    await ensureFreshToken(params);
    return await axios.get(url, params);
  } catch (err) {
    if (err?.code && err?.message && err?.response?.status !== 401) {
      return { error: { message: err.message, code: err.code } };
    }
    const {
      response: { data, status },
    } = err;
    ReactGA.event({
      category: "API call error",
      action: "GET API CALL Fail" + url,
      label: url,
      value: status,
    });

    if (status === 401) {
      const refreshed = await getRefreshedToken();
      if (refreshed?.idToken) {
        // Retry the request
        params.headers = {
          ...params.headers,
          Authorization: refreshed.idToken,
          AppTypeId: webAppType,
        };
        return await axios.get(url, params);
      }
      window.location.href = "/access-denied";
      return;
    }
    if (status === 404) {
      return { error: { message: "Not Found", code: "NotFound" } };
    } else {
      return { error: { data, status } };
    }
  }
};

export const post = async (url, data, params) => {
  ReactGA.event({
    category: "API call error",
    action: "Post API CALL Fail" + url,
    label: url,
    // value:status
  });
  try {
    params.headers = { ...params.headers, AppTypeId: webAppType };
    await ensureFreshToken(params);
    return await axios.post(url, data, params);
  } catch (err) {
    if (!err?.response) {
      return { error: { message: err?.message, code: err?.code } };
    }
    const {
      response: { data: errorData, status },
    } = err;
    ReactGA.event({
      category: "API call error",
      action: "Post API CALL Fail" + url,
      label: url,
      value: status,
    });
    if (status === 401) {
      const refreshed = await getRefreshedToken();
      if (refreshed?.idToken) {
        // Retry the request
        params.headers = {
          ...params.headers,
          Authorization: refreshed.idToken,
          AppTypeId: webAppType,
        };
        return await axios.post(url, data, params);
      }
      window.location.href = "/access-denied";
    } else if (status === 404) {
      return { error: { message: "Not Found", code: "NotFound" } };
    } else {
      return { error: { data: errorData, status } };
    }
  }
};

export const put = async (url, data, params) => {
  try {
    params.headers = { ...params.headers, AppTypeId: webAppType };
    await ensureFreshToken(params);
    return await axios.put(url, data, params);
  } catch (err) {
    if (!err?.response) {
      return { error: { message: err?.message, code: err?.code } };
    }
    const {
      response: { data: errorData, status },
    } = err;
    ReactGA.event({
      category: "API call error",
      action: "Put API CALL Fail" + url,
      label: url,
      value: status,
    });
    if (status === 401) {
      const refreshed = await getRefreshedToken();
      if (refreshed?.idToken) {
        // Retry the request
        params.headers = {
          ...params.headers,
          Authorization: refreshed.idToken,
          AppTypeId: webAppType,
        };
        return await axios.put(url, data, params);
      }
      window.location.href = "/access-denied";
    } else if (status === 404) {
      return { error: { message: "Not Found", code: "NotFound" } };
    } else {
      return { error: { data: errorData, status } };
    }
  }
};

export const deletes = async (url, params, data) => {
  ReactGA.event({
    category: "API call error",
    action: url,
    label: "API CALL",
  });
  try {
    params.headers = { ...params.headers, AppTypeId: webAppType };
    await ensureFreshToken(params);
    return await axios.delete(url, params, data);
  } catch (err) {
    if (!err?.response) {
      return { error: { message: err?.message, code: err?.code } };
    }
    const {
      response: { data: errorData, status },
    } = err;
    ReactGA.event({
      category: "API call error",
      action: "Delete API CALL Fail" + url,
      label: url,
      value: status,
    });
    if (status === 401) {
      const refreshed = await getRefreshedToken();
      if (refreshed?.idToken) {
        // Retry the request
        params.headers = {
          ...params.headers,
          Authorization: refreshed.idToken,
          AppTypeId: webAppType,
        };
        return await axios.delete(url, params, data);
      }
      window.location.href = "/access-denied";
    } else if (status === 404) {
      return { error: { message: "Not Found", code: "NotFound" } };
    } else {
      return { error: { data: errorData, status } };
    }
  }
};
